import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { FileUpload } from '@shared/types/fieldValues';
import { isImage } from '@shared/util/media/type';
import { FileItem } from './FileItem';
import styles from './FileList.module.css';

interface FilesProps {
  files: FileUpload[];
  maxFiles?: number;
  editable: boolean;
  showFileIcon?: boolean;
  onFileRemoved?: (file: FileUpload) => void;
  layout?: 'row' | 'column';
}

export function FileList({
  files,
  maxFiles = Infinity,
  onFileRemoved,
  editable,
  showFileIcon = true,
  layout = 'row',
}: FilesProps) {
  const isRow = layout === 'row';
  const isColumn = layout === 'column';
  const nonImages = useMemo(() => files.filter(file => !isImage(file)), [files]);
  const filesToShow = useMemo(() => nonImages.slice(0, maxFiles), [nonImages, maxFiles]);

  if (isEmpty(nonImages)) {
    return null;
  }

  return (
    <View gap={1} className={[styles.fileList, isRow && styles.row, isColumn && styles.column]}>
      {filesToShow.map(item => (
        <FileItem
          key={item.url}
          file={item}
          editable={editable}
          showFileIcon={showFileIcon}
          onClear={() => onFileRemoved?.(item)}
        />
      ))}
      <FileListFooter files={nonImages} maxFiles={maxFiles} />
    </View>
  );
}

function FileListFooter({ files, maxFiles }: { files: FileUpload[]; maxFiles: number }) {
  const extraFilesCount = files.length - maxFiles;
  if (extraFilesCount < 1) {
    return null;
  }
  return <Pill noLeftMargin>{`+${extraFilesCount}`}</Pill>;
}
